import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { catchError, map } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { GridSettings } from '../../../shared/model/grid-settings.interface';
import { NgSelectOptionModel } from '../../../shared/model/ng-select-option.model';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { SortUtils } from '../../../shared/utils/sort.utils';
import { AssetService } from '../../asset/service/asset.service';
import { ProductSearchModel } from '../model/product-search.model';
import { PartAndProductModelUtil, ProductModel } from '../model/product.model';
import { ProductWarrantyDuration } from '../model/product_warranty_duration';
import { VendorModel } from '../model/vendor.model';
import { ServiceHelper } from './service.helper';

/**
 * @name ProductService
 */
@Injectable()
export class ProductService {
  private serviceHelper;
  private endpointProduct = RestApiModel.PROXY_API_URL + '/product/';
  private endpointGetProduct =
    RestApiModel.PROXY_API_URL + '/assetService/product/';
  private endpointProducts = RestApiModel.PROXY_API_URL + '/products/';
  private endpointLaborInformation =
    RestApiModel.PROXY_API_URL + '/LaborInformation/';
  private endpointProductAttribute =
    RestApiModel.PROXY_API_URL + '/productAttribute/';
  private PRODUCT_LINES_OF_SERVICE_URL =
    RestApiModel.PROXY_API_URL + '/product/';

  private PRODUCT_DURATION_URL =
    RestApiModel.PROXY_API_URL + '/assetService/productWarrantyDuration/';
  private EWARRANTY_DURATION_URL =
    RestApiModel.PROXY_API_URL +
    '/assetService/eWarranty/postEwarrantyDuration';
  private KEYWORDS_URL = RestApiModel.PROXY_API_URL + '/product/';
  public mapResult: Function;
  public sortResult: Function;
  public static PRODUCT_SEARCH_KEY = '_productSearch';
  public static PRODUCT_SEARCH_GRID_KEY = '_productSearchGrid';

  // Resolve HTTP using the constructor
  constructor(private http: HttpClient, private assetService: AssetService) {
    this.serviceHelper = new ServiceHelper(this.http);
    this.mapResult = this.serviceHelper.utils.mapResult.bind(this);
    this.sortResult = this.serviceHelper.utils.sortResult.bind(this);
  }

  /**
   * Return the list of types.
   * @returns {Array<{id: number; text: string}>}
   */
  getTypes(): Array<{ id: number; text: string }> {
    return [{ id: 1, text: 'Part' }, { id: 2, text: 'Machine' }];
  }

  /**
   * If no endDate is provided we assume that Product is Active.
   * If it has a valid End Date then we compare if endDate is bigger then the Current date.
   * @returns {boolean}
   */
  isProductActive(endDate: Date): boolean {
    if (!endDate || !this.serviceHelper.utils.isValidateDateObject(endDate)) {
      return true;
    } else {
      return new Date(endDate) > new Date();
    }
  }

  /**
   *  Get the mapped search result
   * @param {ProductSearchModel} searchModel
   * @param {any} res
   * @returns {any}
   */
  getSearchResultModel(searchModel: ProductSearchModel, res: any) {
    return this.serviceHelper.mapData.mapSearchResult(searchModel, res);
  }

  /**
   * Execute the Search Based on the Parameters from the Filter
   */
  searchPartAndProduct(searchModel: ProductSearchModel): Observable<any> {
    let queryStream = this.getProductSearchQueryStream(searchModel);

    return this.http
      .get(RestApiModel.PROXY_API_URL + '/products' + queryStream)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * TODO: document this.
   * @param {ProductSearchModel} searchModel
   * @param {number} maxRows
   * @returns {Observable<any>}
   */
  searchPartAndProductWithMaxRows(
    searchModel: ProductSearchModel,
    maxRows: number
  ): Observable<any> {
    let queryStream = this.getProductSearchQueryStream(searchModel);

    queryStream = queryStream + '&maxRows=' + maxRows;

    return this.http
      .get(RestApiModel.PROXY_API_URL + '/products' + queryStream)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get All Line of Services.
   * @returns {Observable<any>}
   */
  getAllLineOfService = (): Observable<any> => {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/linesOfService')
      .map((res: any) => res)
      .catch((error: any) => error);
  };

  /**
   * Same as above but returns the result in a ng-select collection format to display.
   * @returns {Observable<any>}
   */
  getAllLinesOfServicesForNgSelect(): Observable<Array<NgSelectOptionModel>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/linesOfService')
      .map((res: any) => {
        let lineOfServices = res.results.map(los => {
          return {
            id: los.id,
            text: los.name
          };
        });
        return lineOfServices.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
      })
      .catch((error: any) => error);
  }

  /**
   * Get All Product Lines.
   * @returns {Observable<any>}
   */
  getAllProductLines = (): Observable<any> => {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/productLines/')
      .map((res: any) => res)
      .catch((error: any) => error);
  };

  /**
   * Same as above but returns the result in a ng-select collection format to display.
   * @returns {Observable<any>}
   */
  getAllProductLinesForNgSelect(): Observable<Array<NgSelectOptionModel>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/productLines/')
      .map((res: any) => {
        let productLines = res.results.map(productLine => {
          return {
            id: productLine.id,
            text: productLine.name
          };
        });
        return productLines.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
      })
      .catch((error: any) => error);
  }

  /**
   * Get All Products Supplied by Customer.
   * @returns {Observable<any>}
   */
  getAllProductsSuppliedByCustomer = (): Observable<any> => {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/productsSuppliedByCustomer/')
      .map((res: any) => res)
      .catch((error: any) => error);
  };

  /**
   * Get the Line of Services that belong to a specific product
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getLOSProductById(productId: number): Observable<any> {
    return this.http
      .get(this.PRODUCT_LINES_OF_SERVICE_URL + productId + '/linesOfService')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get the Product Duration (Labor Waranty Days),(Part Waranty Days) that belong to a specific product
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getProductDurationDaysById(productId: number): Observable<any> {
    return this.http
      .get(this.PRODUCT_DURATION_URL + productId)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Edit the Product Duration (Labor Waranty Days),(Part Waranty Days) that belong to a specific product
   * @param {ProductWarrantyDuration} ewaranty
   * @returns {Observable<any>}
   */
  editeWarrantyDuration(eWarranty: ProductWarrantyDuration): Observable<any> {
    let request: any = {
      productId: eWarranty.productId,
      laborWarrantyDays: eWarranty.laborWarrantyDays,
      partWarrantyDays: eWarranty.partsWarrantyDays
    };
    return this.http
      .post(this.EWARRANTY_DURATION_URL, request)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get the keywordData that belong to a specific product
   * @param {number} productId
   * @returns {Observable<any>}
   */
  getKeywordProductById(productId: number): Observable<any> {
    return this.http.get(this.KEYWORDS_URL + productId + '/keywords').pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Create and Delete line of service from an Array of Elements
   * @returns {Observable<any>}
   */
  updateLineOfServiceForAProduct(
    productId: number,
    newLineOfService: any[],
    deleteLineOfService: any[]
  ): Observable<any> {
    let losProcess = this.saveAndDeleteItems(
      productId,
      newLineOfService,
      deleteLineOfService,
      this.serviceHelper.addition.saveLineOfServiceProduct,
      this.serviceHelper.deletion.deleteLineOfServiceProduct
    );
    return Observable.forkJoin(losProcess);
  }

  /**
   * Create and Delete Product Line from an Array of Elements.
   * @returns {Observable<any>}
   */
  updateProductLineForAProduct(
    productId: number,
    newProductLine: any[],
    deleteProductLine: any[]
  ): Observable<any> {
    let productLineProcess = this.saveAndDeleteItems(
      productId,
      newProductLine,
      deleteProductLine,
      this.serviceHelper.addition.saveProductLineProduct,
      this.serviceHelper.deletion.deleteProductLineProduct
    );
    return Observable.forkJoin(productLineProcess);
  }

  /**
   * Create and Delete Product Supplied by a customer from an Array of Elements...
   * @returns {Observable<any>}
   */
  updatePSBCForAProduct(
    productId: number,
    newPSBC: any[],
    deletePSBC: any[]
  ): Observable<any> {
    let PSBCProcess = this.saveAndDeleteItems(
      productId,
      newPSBC,
      deletePSBC,
      this.serviceHelper.addition.savePSBCForProduct,
      this.serviceHelper.deletion.deletePSBCForProduct
    );
    return Observable.forkJoin(PSBCProcess);
  }

  /**
   * Add and delete items from the given Product.
   * @returns {Observable<any>}
   */
  saveAndDeleteItems(
    productId: number,
    newItems: any[],
    deleteItems: any[],
    addFunction: Function,
    deleteFunction: Function
  ) {
    let productLineProcess = [];
    // 1.- We Save the New Values
    newItems.forEach((item: any) => {
      productLineProcess.push(addFunction(productId, item.id));
    });
    // 2.- We Delete the Old Values
    deleteItems.forEach((item: any) => {
      productLineProcess.push(deleteFunction(productId, item.id));
    });
    return productLineProcess;
  }

  /**
   * Get All Product Types
   * @returns {Observable<any>}
   */
  getAllProductTypes(): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/ProductAttributes?typeId=0-6DJ6K')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get All Product Types
   * @returns {Observable<any>}
   */
  getAllVendors(): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/vendors')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get the Query Stream required for the Product Search
   * @param {ProductSearchModel} searchModel
   * @returns {String}
   */
  getProductSearchQueryStream(searchModel: ProductSearchModel): String {
    let query = '';
    for (let p in searchModel.filterModel) {
      if (searchModel.filterModel.hasOwnProperty(p)) {
        if (searchModel.filterModel[p] && searchModel.filterModel[p] !== '') {
          if (
            this.serviceHelper.utils.isValidateDateObject(
              searchModel.filterModel[p].toString()
            )
          ) {
            query += this.serviceHelper.utils.getDateParam(searchModel, p);
          } else if (p === 'model') {
            query += p + `=${searchModel.filterModel[p].toString().trim()}&`;
          } else {
            // Ensure the property is string and we can trim it.
            query +=
              p + '=' + searchModel.filterModel[p].toString().trim() + '&';
          }
        }
      }
    }
    if (query.indexOf('showInactive') === -1) {
      query += 'showInactive=false&';
    }
    if (query.indexOf('requiresAudit=') >= 0) {
      query = query.replace('requiresAudit=Yes', 'requiresAudit=true');
      query = query.replace('requiresAudit=No', 'requiresAudit=false');
    }
    // query = '?' + query + 'pageNumber=' + searchModel.pageNumber + '&pageSize=searchModel.';
    query = `?${query}pageNumber=${searchModel.pageNumber}&pageSize=${
      searchModel.pageSize ? searchModel.pageSize : '100'
    }`;

    return query;
  }

  /**
   * Get the entire Map of Product by the ID
   * Iterate the object to obtain each sub-sequence of Request URLs
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getAllDataProductById(productId: number): Observable<ProductModel> {
    // Create the Initial Chain of Object depending if the value of the request url contains the Product ID
    let observablesProductChainInfo = Observable.forkJoin([
      /* [0] */ this.getProductById(productId)
      /* [1]  this.getLaborInformationProductById(productId),*/
      /* [2]  this.getProductLinesByProductId(productId)*/
    ]);

    // let productTypeId: string;
    let productDetailRequest = [];
    let partAndProductModel: ProductModel;

    // Execute the Fork Join to get each value, the index on the Join should match response index
    let firstProductChainInfo = Observable.from(
      observablesProductChainInfo
    ).map((res: any) => {
      // Getting Mapped Data
      partAndProductModel = this.serviceHelper.mapData.mapPartAndProductModel(
        partAndProductModel,
        res
      );
      return partAndProductModel;
    });

    // Subscribe the Result (FlatMap executes an inner subscription)
    /*let linkRequests = firstProductChainInfo.flatMap((res: ProductModel) => {
      partAndProductModel = res;
      productDetailRequest.push(this.getProductAttributeById(partAndProductModel.productType));
      return Observable.forkJoin(productDetailRequest);
    });*/

    // Execute the inner Fork Join, index should matchs the original Join from Link Request
    /*return Observable.from(linkRequests).map((res: any) => {
      return partAndProductModel;
    })*/
    // Execute the inner Fork Join, index should match the original Join from Link Request
    return Observable.from(firstProductChainInfo).map((res: any) => {
      return partAndProductModel;
    });
  }

  /**
   * Get the entire Map of Product Machine Data.
   * @param {string} product
   * @returns {Observable<any>}
   */
  getProductMachineData(product: ProductModel): Observable<ProductModel> {
    // Create the Initial Chain of Object depending if the value of the request url contains the Product ID
    let observablesProductChainInfo = Observable.forkJoin([
      /* [0] */ this.getProductById(product.id),
      /* [1] */ this.assetService.getAssetType(product.assetTypeId),
      /* [2] */ this.assetService.getMakeById(product.makeId)
    ]);
    let partAndProductModel: ProductModel;
    // Execute the Fork Join to get each value, the index on the Join should match response index
    let firstProductChainInfo = Observable.from(
      observablesProductChainInfo
    ).map((res: any) => {
      console.log(res);
      partAndProductModel = new ProductModel();
      if (res[0]) {
        partAndProductModel = res[0];
        partAndProductModel.startDate = res[0].startDate
          ? new Date(res[0].startDate)
          : null;
        partAndProductModel.endDate = res[0].endDate
          ? new Date(res[0].endDate)
          : null;
        if (partAndProductModel.makeId <= 0) {
          partAndProductModel.makeId = null;
        }
        // if (!partAndProductModel.productType) {
        //  partAndProductModel.productType = 'Unknown';
        // }
      }
      if (res[1]) {
        partAndProductModel.assetType = res[1];
      }
      if (res[2]) {
        partAndProductModel.make = res[2];
      }
      return partAndProductModel;
    });
    // Execute the inner Fork Join, index should match the original Join from Link Request
    return Observable.from(firstProductChainInfo).map((res: any) => {
      return partAndProductModel;
    });
  }

  /**
   * Gets the basic properties of a Product by using the given ID
   * @param productId
   * @returns {Observable<ProductModel>}
   */
  getBasicProductProperties(productId): Observable<ProductModel> {
    let partAndProductModel = new ProductModel();
    let subject = new Subject<ProductModel>();
    this.getProductById(productId).subscribe((res: any) => {
      if (res) {
        partAndProductModel = res;
        partAndProductModel.startDate = res.startDate
          ? new Date(res.startDate)
          : null;
        partAndProductModel.endDate = res.endDate
          ? new Date(res.endDate)
          : null;
        if (partAndProductModel.makeId <= 0) {
          partAndProductModel.makeId = null;
        }
      }
      subject.next(partAndProductModel);
    });
    return subject.asObservable();
  }

  /**
   * TODO: document this.
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getProductById(productId: number): Observable<any> {
    return this.http
      .get(this.endpointGetProduct + productId + '?showInactive=true')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get Labor Information of a Product
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getLaborInformationProductById(productId: number): Observable<any> {
    return this.http
      .get(this.endpointLaborInformation + productId)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get Product Attribute by Id
   * @param {string} productTypeId
   * @returns {Observable<any>}
   */
  getProductAttributeById(productTypeId: string): Observable<any> {
    return this.http
      .get(this.endpointProductAttribute + productTypeId)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get a product by its name.
   * @param {string} productName
   * @returns {Observable<any>}
   */
  getProductByProductName(productName: string): Observable<any> {
    return this.http
      .get(this.endpointProducts + '?name=' + productName)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * CREATE a new Product.
   * @param {ProductModel} productModel
   * @returns {Observable<any>}
   */
  createProduct(productModel: ProductModel): Observable<any> {
    let request: any = {
      makeId: productModel.makeId,
      model: productModel.model,
      partNumber: productModel.partNumber,
      requiresAudit: productModel.requiresAudit,
      type: productModel.type,
      typeId: productModel.typeId,
      assetTypeId: productModel.assetTypeId
    };
    return this.http
      .post(this.endpointProduct, request)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * UPDATE an existing product. It should update any product property/properties, them should pass all together as an object.
   * @param {object} productModel
   * @returns {Observable<any>}
   */
  updateProductByProperty(
    productModel: object,
    productId: string
  ): Observable<any> {
    return this.http
      .put(this.endpointProduct + productId, JSON.stringify(productModel))
      .map((res: any) => res)
      .catch((error: any) => {
        console.log(error);
        return error;
      });
  }

  /**
   * UPDATE an existing product.
   * @param {ProductModel} productModel
   * @returns {Observable<any>}
   */
  saveProduct(productModel: ProductModel): Observable<any> {
    let request: any = {
      id: productModel.id,
      makeId: productModel.makeId,
      model: productModel.model,
      partNumber: productModel.partNumber,
      requiresAudit: productModel.requiresAudit,
      type: productModel.type,
      typeId: productModel.typeId,
      assetTypeId: productModel.assetTypeId,
      endDate: productModel.endDate
    };

    return this.http
      .put(this.endpointProduct + productModel.id, request)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * CREATE and attach LaborInformation to a product.
   * @param {ProductModel} productModel
   * @param {string} productId
   * @returns {Observable<any | any>}
   */
  createLaborInformation(productModel: ProductModel, productId: string) {
    let saveModel = PartAndProductModelUtil.getLaborInformationModelToCreate(
      productModel,
      productId
    );
    return this.http
      .post(this.endpointLaborInformation, JSON.stringify(saveModel))
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * UPDATE a LaborInformation of a product.
   * @param {ProductModel} productModel
   * @param {string} productId
   * @returns {Observable<any | any>}
   */
  saveLaborInformation(productModel: ProductModel, productId: string) {
    let saveModel = PartAndProductModelUtil.getLaborInformationModelToCreate(
      productModel,
      productId
    );
    return this.http
      .put(this.endpointLaborInformation + productId, JSON.stringify(saveModel))
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Delete a Product by ID
   * @param {string} productId
   */
  deleteProduct(productId: number): Observable<any> {
    return this.http
      .delete(this.endpointProduct + productId)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get a list of Part and Products to delete, iterate the element and make a request per element
   * @param {ProductModel[]} productList
   * @returns {Observable<any>}
   */
  deleteListOfProducts(productList: ProductModel[]): Observable<any> {
    let productBulkActionDelete = [];
    productList.forEach(product => {
      productBulkActionDelete.push(this.deleteProduct(product.id));
    });
    return Observable.forkJoin(productBulkActionDelete);
  }

  /**
   * The NGX-DATATBLE Supports a Detail Card View
   * that holds information of a product with n request url attached to the object
   * This method prepares call for each one and attachs the new values to the object
   * @param row
   * @returns {Observable<any>}
   */
  getProductDetailCardView(row: any): Observable<any> {
    return this.getAllDataProductById(row.id);
  }

  /**
   * Get Product Line of a Product
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getProductLinesByProductId(productId: number): Observable<any[]> {
    return this.http
      .get(this.endpointProduct + productId + '/productLines')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get Product Line of a Product
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getPSBCByProductId(productId: string): Observable<any[]> {
    return this.http
      .get(this.endpointProduct + productId + '/ProductsSuppliedByACustomer')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  /**
   * Get - Associated Vendors from a product.
   * Uses the allVendors list to search for the vendor name.
   * If no match is found or list is not provided Vendor model only will contain it's id.
   * @param {string} productId
   * @returns {Observable<any>}
   */
  getAssociatedVendors(
    productId: number,
    allVendors: Array<VendorModel>
  ): Observable<any> {
    return this.http
      .get(this.endpointProduct + `${productId}/vendors`)
      .map((res: any) => {
        if (allVendors && allVendors.length > 0) {
          let linkedVendors: Array<VendorModel> = res.results;
          for (let vendor of linkedVendors) {
            const matchFound = allVendors.find(item => item.id === vendor.id);
            vendor.name = '';
            if (matchFound) {
              vendor.name = matchFound.name;
            }
          }
          return linkedVendors;
        } else {
          return res.results;
        }
      })
      .catch((error: any) => error);
  }

  /**
   * Get - List of all Makes
   * @returns {Observable<any>}
   */
  getProductMakes(): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/makes')
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  getProductMakesForNgSelect(): Observable<Array<NgSelectOptionModel>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/makes')
      .map((res: any) => {
        let results = res.results.map(item => {
          return {
            id: item.id,
            text: item.name
          };
        });
        return results.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'text')
        );
      })
      .catch((error: any) => error);
  }

  /**
   * Stores the product search model on local storage.
   * @param input
   */
  setProductSearchInput(input: ProductSearchModel): void {
    localStorage.setItem(
      ProductService.PRODUCT_SEARCH_KEY,
      JSON.stringify(input)
    );
  }

  /**
   * Retrieves the product search model from local storage if exists.
   */
  getProductSearchInput(): ProductSearchModel {
    if (localStorage.getItem(ProductService.PRODUCT_SEARCH_KEY)) {
      return JSON.parse(
        localStorage.getItem(ProductService.PRODUCT_SEARCH_KEY)
      );
    } else {
      return new ProductSearchModel();
    }
  }

  /**
   * Stores the Grid Settings on local storage.
   * @param input
   */
  setStatePersistingInputs(input: GridSettings) {
    localStorage.setItem(
      ProductService.PRODUCT_SEARCH_GRID_KEY,
      JSON.stringify(input)
    );
  }

  /**
   * Retrieves Grid Settings model from local storage.
   */
  getStatePersistingInputs(): GridSettings {
    if (localStorage.getItem(ProductService.PRODUCT_SEARCH_GRID_KEY)) {
      return JSON.parse(
        localStorage.getItem(ProductService.PRODUCT_SEARCH_GRID_KEY)
      );
    } else {
      return null;
    }
  }
}
